import clsx from 'clsx';
import { statusColor } from './statusColor';
import type { CategoryMapPoint } from './types';

export function CategoryMapWorklist({
  points,
  emphasizedCategoryIds,
  loading,
  error,
  hoveredId,
  onHover,
  onSelect,
}: {
  points: CategoryMapPoint[];
  emphasizedCategoryIds: Set<number> | null;
  loading: boolean;
  error: boolean;
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  onSelect: (clusterId: number) => void;
}) {
  if (loading) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">Loading…</p>;
  }

  if (error) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">Couldn’t load candidates.</p>;
  }

  const candidates = points
    .filter((point) => point.status !== 'normal')
    .filter((point) => emphasizedCategoryIds === null || emphasizedCategoryIds.has(point.categoryId))
    .sort((a, b) => b.margin - a.margin);

  if (candidates.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">No mislabel candidates.</p>;
  }

  return (
    <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
      {candidates.map((point) => (
        <li key={point.id}>
          <button
            type="button"
            onMouseEnter={() => onHover(point.id)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onSelect(point.id)}
            className={clsx(
              'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs',
              hoveredId === point.id
                ? 'border-gray-300 bg-gray-50 dark:border-zinc-600 dark:bg-zinc-700/50'
                : 'border-transparent',
            )}
          >
            <span
              className="inline-block size-2 shrink-0 rounded-full"
              style={{ backgroundColor: statusColor(point.status) }}
            />
            <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-zinc-100">
              #{point.id} {point.categoryName}
              <span className="text-gray-400 dark:text-zinc-500"> → {point.suggestedCategoryName}</span>
            </span>
            <span className="shrink-0 tabular-nums text-gray-400 dark:text-zinc-500">
              {point.margin.toFixed(3)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
